import {
  IAction,
  type IStep,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import CopyButton from '@/components/CopyButton';
import { getNavigationAction, isNavigationAction } from '@/lib/navigation';
import { IPMA_SOURCE_ACTION, getIpmaSourceAction } from '@/lib/sources';

import MessageActions from './Actions';
import { DebugButton } from './DebugButton';
import { FeedbackButtons } from './FeedbackButtons';
import { SourceLinks } from './SourceLinks';

interface Props {
  message: IStep;
  actions: IAction[];
  run?: IStep;
  contentRef?: React.RefObject<HTMLDivElement>;
}

const CERTIFICATION_FINDER_ACTION = 'certification_finder_answer';

const MessageButtons = ({ message, actions, run, contentRef }: Props) => {
  const { config } = useConfig();
  const { firstInteraction } = useChatMessages();

  const isUser = message.type === 'user_message';
  const isAsk = message.waitForAnswer;
  const hasContent = !!message.output;
  const showCopyButton = !!run && hasContent && !isUser && !isAsk;

  const messageActions = actions.filter((a) => a.forId === message.id);
  const navigationActions = messageActions.filter(
    (action) => getNavigationAction(action)?.behavior === 'button'
  );
  const sourceActions = messageActions
    .map(getIpmaSourceAction)
    .filter((source): source is NonNullable<typeof source> => source !== null);
  const finderActions = messageActions.filter(
    (action) => action.name === CERTIFICATION_FINDER_ACTION
  );
  const standardActions = messageActions.filter(
    (action) =>
      !isNavigationAction(action) &&
      action.name !== IPMA_SOURCE_ACTION &&
      action.name !== CERTIFICATION_FINDER_ACTION
  );

  const showDebugButton =
    !!config?.debugUrl && !!message.threadId && !!firstInteraction && !!run;

  const showStandardControls =
    !finderActions.length &&
    (showCopyButton || showDebugButton || standardActions.length);
  const show =
    showStandardControls ||
    navigationActions.length ||
    sourceActions.length ||
    finderActions.length;

  if (!show || message.streaming) {
    return null;
  }

  return (
    <>
      {sourceActions.length ? <SourceLinks sources={sourceActions} /> : null}
      {navigationActions.length ? (
        <div className="mt-1.5 flex min-h-8 flex-wrap items-center gap-1">
          <MessageActions actions={navigationActions} />
        </div>
      ) : null}
      {finderActions.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <MessageActions actions={finderActions} />
        </div>
      ) : null}
      {showStandardControls ? (
        <div className="-ml-1 mt-1 flex min-h-7 flex-wrap items-center gap-0 opacity-45 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100">
          {showCopyButton ? (
            <CopyButton content={message.output} contentRef={contentRef} />
          ) : null}
          {run ? <FeedbackButtons message={run} /> : null}
          {standardActions.length ? (
            <MessageActions actions={standardActions} />
          ) : null}
          {showDebugButton ? (
            <DebugButton debugUrl={config.debugUrl!} step={message} />
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export { MessageButtons };
